/**
 * Custom AG Grid aggregation functions for aerodynamic data.
 *
 * Adapted from flexfoil's aggFunctions — provides argmax/argmin style
 * aggregations so grouped rows show summary values like α at CL_max.
 */

import type { IAggFunc, IAggFuncParams } from "ag-grid-community";

function getLeafValues(params: IAggFuncParams): unknown[] {
  const vals: unknown[] = [];
  if (!params.rowNode?.allLeafChildren) return vals;
  for (const child of params.rowNode.allLeafChildren) {
    if (child.data) vals.push(child.data);
  }
  return vals;
}

function atExtremum(
  params: IAggFuncParams,
  targetField: string,
  mode: "max" | "min",
  returnField: string,
): number | null {
  const rows = getLeafValues(params) as Record<string, unknown>[];
  let best: number | null = null;
  let bestReturn: number | null = null;

  for (const row of rows) {
    const tv = row[targetField];
    const rv = row[returnField];
    if (typeof tv !== "number" || typeof rv !== "number") continue;
    if (best === null || (mode === "max" ? tv > best : tv < best)) {
      best = tv;
      bestReturn = rv;
    }
  }
  return bestReturn;
}

export const aggAtMaxCL: IAggFunc = (params) => atExtremum(params, "CL", "max", "alpha");
export const aggAtMaxLD: IAggFunc = (params) => {
  const rows = getLeafValues(params) as Record<string, unknown>[];
  let bestLD = -Infinity;
  let bestAlpha: number | null = null;
  for (const row of rows) {
    const cl = row.CL;
    const cd = row.CD;
    const alpha = row.alpha;
    if (typeof cl !== "number" || typeof cd !== "number" || typeof alpha !== "number") continue;
    if (cd <= 0) continue;
    const ld = cl / cd;
    if (ld > bestLD) {
      bestLD = ld;
      bestAlpha = alpha;
    }
  }
  return bestAlpha;
};

export const ALL_CUSTOM_AGGS: Record<string, IAggFunc> = {
  "α at CL_max": aggAtMaxCL,
  "α at L/D_max": aggAtMaxLD,
};

export const ALL_AGG_FUNC_NAMES = ["sum", "min", "max", "avg", "count", ...Object.keys(ALL_CUSTOM_AGGS)];
